import styled from '@emotion/styled';
import React, { useState, useRef, useEffect } from 'react';
import { Box, GreenButton, RedButton } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useLogger } from '~Hooks';
import QuestionModal from '~ComponentsImproved/QuestionModal';
import useModal from '~ComponentsImproved/Modal/useModal';
import useI18n from '~Translations';
import { keyframes } from '@emotion/react';
import { formatDateDisplay, formatPhoneNumber, isToday } from '../utils';
import Icon from '~ComponentsImproved/Icon';
import CallIcon from '~Assets/icons/call.svg';
import MailIcon from '~Assets/icons/mail.svg';
import { BookingComment, DiscountBadge, IconText } from '../sharedStyles';
import { useBookings } from '~Providers/BookingsProvider';
import { GetIncomingBookings_operativeViewer_incomingBookingsForVenue } from '~Providers/BookingsProvider/api/__queries__';

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const BookingCard = styled(Box)<{ isAccepting?: boolean; highlight?: boolean }>`
  background-color: var(--colorBlueGray1200);
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  position: relative;
  opacity: ${(props) => (props.isAccepting ? 0.6 : 1)};
  transition: opacity 0.3s ease;
  pointer-events: ${(props) => (props.isAccepting ? 'none' : 'auto')};
  outline: ${({ highlight }) =>
    highlight
      ? '1px solid var(--colorUIYellow)'
      : 'none'}; /* Highlight with a solid border when selected */
`;

const LoadingSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 40px;
  border: 4px solid var(--colorBlueGray1100);
  border-top-color: var(--colorUIGreen);
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
  z-index: 10;
`;

const BookingHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const BookingTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: space-between;
`;

const BookingTime = styled(Paragraph)`
  margin: 0;
  font-weight: 700;
  font-size: 16px;
`;

const PartySizeText = styled(Paragraph)`
  margin: 0;
  font-weight: 600;
  opacity: 0.8;
`;

const ButtonsContainer = styled(Box)`
  display: flex;
  gap: 8px;
  margin-top: 12px;
`;

const AcceptButton = styled(GreenButton)`
  flex: 1;
`;

const DenyButton = styled(RedButton)`
  flex: 1;
`;

// Props for BookingCardItem component
interface Props {
  booking: GetIncomingBookings_operativeViewer_incomingBookingsForVenue;
  onAcceptBooking: (booking: GetIncomingBookings_operativeViewer_incomingBookingsForVenue) => void;
}

// Individual booking card component to avoid hooks in loops
const IncomingBooking: React.FC<Props> = ({ booking, onAcceptBooking }) => {
  const logger = useLogger('bookings');
  const { i18n } = useI18n();
  const denyModal = useModal();
  const [isProcessing, setIsProcessing] = useState(false);
  const bookings = useBookings();
  const cardRef = useRef<HTMLDivElement>(null);

  // Scroll into view when this booking is highlighted
  useEffect(() => {
    if (bookings.highlightedIncomingBookingId === booking.id && cardRef.current) {
      cardRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [bookings.highlightedIncomingBookingId, booking.id, logger]);

  // Handle accept booking
  const handleAccept = async () => {
    bookings.actions.setHighlightedIncomingBooking(booking.id);
    setIsProcessing(true);
    const acceptedBooking = await bookings.actions.accept(booking.id, booking.date);

    if (acceptedBooking) {
      onAcceptBooking(booking);
    }

    setIsProcessing(false);
  };

  // Handle deny booking
  const handleDeny = async () => {
    bookings.actions.setHighlightedIncomingBooking(booking.id);
    denyModal.toggle();
    setIsProcessing(true);
    await bookings.actions.reject(booking.id, booking.date);

    setIsProcessing(false);
  };

  // Create accept button with useButton hook
  const acceptButton = useButton({
    onClick: handleAccept,
    disabled: isProcessing,
  });

  // Create deny button with useButton hook
  const denyButton = useButton({
    onClick: () => {
      if (!isProcessing) {
        logger.info('Opening deny confirmation', { bookingId: booking.id });
        denyModal.toggle();
      }
    },
    disabled: isProcessing,
  });

  const bookingIsForToday = isToday(booking.date);

  return (
    <>
      <BookingCard
        ref={cardRef}
        isAccepting={isProcessing}
        highlight={bookings.highlightedIncomingBookingId === booking.id}
        onClick={() => bookings.actions.setHighlightedIncomingBooking(booking.id)}>
        {isProcessing && <LoadingSpinner />}

        {/* Title: Date and Party Size */}
        <BookingHeader>
          <BookingTitle>
            <BookingTime level={3} color="var(--colorWhite)">
              <span
                style={{
                  color: bookingIsForToday ? 'var(--colorUISand)' : 'var(--colorWhite)',
                  fontWeight: bookingIsForToday ? 'bold' : 'normal',
                }}>
                {formatDateDisplay(booking)}
              </span>{' '}
              • {booking.startTime} - {booking.endTime}
            </BookingTime>
            {booking.discountPercent && booking.discountPercent > 0 ? (
              <DiscountBadge>
                {booking.discountPercent}
                {i18n('Bookings.PercentOff')}
              </DiscountBadge>
            ) : null}
          </BookingTitle>
        </BookingHeader>

        {/* Name and Discount */}
        <Box>
          <Box display="flex" justifyContent="space-between" gap="xs" marginBottom="xs">
            <Paragraph level={3} color="var(--colorBlueGray300)">
              {booking.guestName || i18n('Bookings.UnknownGuest')}
            </Paragraph>
            <PartySizeText level={3} color="var(--colorBlueGray300)">
              {booking.partySize} {i18n('Bookings.Guests')}
            </PartySizeText>
          </Box>

          {booking.guestPhone && (
            <IconText>
              <Icon src={CallIcon} color="var(--colorBlueGray400)" height="16px" width="16px" />
              {formatPhoneNumber(booking.guestPhone)}
            </IconText>
          )}

          {booking.guestEmail && (
            <IconText>
              <Icon src={MailIcon} color="var(--colorBlueGray400)" height="16px" width="16px" />
              {booking.guestEmail}
            </IconText>
          )}
        </Box>

        {/* Special Request */}
        {booking.comment && <BookingComment>{booking.comment}</BookingComment>}

        {/* Buttons */}
        <ButtonsContainer>
          <DenyButton {...denyButton}>
            {isProcessing ? i18n('Bookings.Loading') : i18n('Bookings.Deny')}
          </DenyButton>
          <AcceptButton {...acceptButton}>
            {isProcessing ? i18n('Bookings.Loading') : i18n('Bookings.Accept')}
          </AcceptButton>
        </ButtonsContainer>
      </BookingCard>

      {/* Confirmation Modal */}
      <QuestionModal
        question={i18n('Bookings.DenyConfirmation')}
        isShowing={denyModal.isShowing}
        onLeftClick={() => denyModal.toggle()}
        onRightClick={handleDeny}
        leftText={i18n('General.Cancel')}
        rightText={i18n('Bookings.DenyBooking')}
      />
    </>
  );
};

export default IncomingBooking;
